/**
 * PinnedCard — même forme qu'AppCard mais ~40% plus grande.
 * Utilisée dans la section "Accès rapide" du tableau de bord.
 */
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PinOff } from 'lucide-react';
import type { AppDefinition, AppStatus } from '../types';
import { useTranslation } from '../i18n';
import { isAppOpenable, openAppWindow } from '../data/catalogVisibility';

const STATUS_UI: Record<AppStatus, { dot: string; text: string }> = {
  prod:        { dot: 'bg-emerald-600 dark:bg-emerald-400', text: 'text-emerald-600 dark:text-emerald-400' },
  integration: { dot: 'bg-amber-500 dark:bg-amber-400',    text: 'text-amber-500 dark:text-amber-400' },
  dev:         { dot: 'bg-slate-400 dark:bg-slate-500',     text: 'text-slate-400 dark:text-slate-500' },
  poc:         { dot: 'bg-violet-500 dark:bg-violet-400',   text: 'text-violet-500 dark:text-violet-400' },
  test:        { dot: 'bg-blue-500 dark:bg-blue-400',       text: 'text-blue-500 dark:text-blue-400' },
};

function DynamicIcon({ name, size, color }: { name: string; size: number; color: string }) {
  const Cmp = (LucideIcons as unknown as Record<string, LucideIcon>)[name];
  const Fallback = LucideIcons.Box;
  const Icon = Cmp ?? Fallback;
  return <Icon size={size} style={{ color }} />;
}

interface PinnedCardProps {
  app: AppDefinition;
  onTogglePin: (id: string) => void;
  userEmail?: string;
}

export function PinnedCard({ app, onTogglePin, userEmail }: PinnedCardProps) {
  const { t } = useTranslation();
  const status = STATUS_UI[app.status];
  const hasUrl = isAppOpenable(app);
  const showAdminBadge = Boolean(app.adminOnly && hasUrl);
  const name = t(`hub:catalog.${app.id}.name`);
  const desc = t(`hub:catalog.${app.id}.desc`);
  const displayName = name.replace(/^GS-/, '');

  // MEME geste que la tuile du catalogue : meme application, donc meme fenetre.
  const openApp = () => { openAppWindow(app, userEmail); };

  return (
    <article
      role={hasUrl ? 'link' : undefined}
      tabIndex={hasUrl ? 0 : undefined}
      onClick={hasUrl ? openApp : undefined}
      onKeyDown={hasUrl ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openApp(); } } : undefined}
      className={`group relative flex w-full flex-col items-center rounded-2xl border border-slate-200 bg-white px-3 py-4 shadow-xs transition-all duration-150 select-none dark:border-slate-700 dark:bg-slate-800 ${
        hasUrl
          ? 'cursor-pointer hover:scale-105 active:scale-95 hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600'
          : 'cursor-not-allowed pointer-events-none opacity-40'
      }`}
    >
      {/* Badge ADMIN */}
      {showAdminBadge && (
        <span className="absolute inset-x-0 top-0 z-10 flex items-center justify-center rounded-t-2xl bg-slate-800/90 py-0.5 text-chip font-semibold uppercase tracking-wide text-white dark:bg-slate-200/90 dark:text-slate-900">
          {t('hub:cards.adminBadge')}
        </span>
      )}

      {/* Bouton dépingler */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onTogglePin(app.id); }}
        className={`absolute right-2 z-20 flex h-6 w-6 items-center justify-center rounded-lg
                    bg-slate-100 text-slate-400
                    opacity-0 group-hover:opacity-100
                    hover:bg-red-50 hover:text-red-500
                    dark:bg-slate-700 dark:text-slate-500
                    dark:hover:bg-red-950/30 dark:hover:text-red-400
                    transition-all duration-150 touch-manipulation
                    ${showAdminBadge ? 'top-6' : 'top-2'}`}
        title={t('hub:cards.unpinQuick')}
      >
        <PinOff size={12} />
      </button>

      {/* Icône — 40% plus grande qu'AppCard (h-10→h-14, size 18→26) */}
      <div
        className="mx-auto mb-2 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl"
        style={{ backgroundColor: app.iconBg }}
      >
        <DynamicIcon name={app.icon} size={26} color={app.iconColor} />
      </div>

      {/* Nom */}
      <span className="w-full truncate text-center text-field font-semibold leading-tight text-slate-800 dark:text-slate-100">
        {displayName}
      </span>

      {/* Description */}
      {desc ? (
        <span className="mt-0.5 line-clamp-2 w-full text-center text-chip leading-tight text-slate-400 dark:text-slate-500">
          {desc}
        </span>
      ) : null}

      {/* Statut */}
      <div className="mt-2 flex items-center gap-1">
        <span className={`h-2 w-2 shrink-0 rounded-full ${status.dot}`} aria-hidden />
        <span className={`text-chip ${status.text}`}>{t(`hub:status.${app.status}`)}</span>
      </div>
    </article>
  );
}
